import React from 'react';
import { useData } from '../../hooks/useData';
import { fmtDate } from '../../utils/helpers';

function DocumentPreviewModal({ open, onClose, document }) {
  const { state } = useData();

  if (!open || !document) return null;

  const owner = state.users.find((u) => String(u.id) === String(document.updatedBy || document.owner));
  const study = state.studies.find((s) => s.id === document.studyId);

  const ext = (document.name.split('.').pop() || '').toLowerCase();
  const isPDF = ext === 'pdf';
  const isDOC = ext === 'doc' || ext === 'docx';
  const isXLS = ext === 'xls' || ext === 'xlsx';
  const iconColor = isPDF ? '#a32d2d' : isDOC ? '#185fa5' : isXLS ? '#0f6e56' : 'var(--ink-3)';
  const iconClass = isPDF ? 'ti-file-type-pdf' : isDOC ? 'ti-file-type-doc' : isXLS ? 'ti-file-type-xls' : 'ti-file';

  const previewBody = () => {
    if (document.fileUrl && isPDF) {
      return (
        <iframe
          title={document.name}
          src={document.fileUrl}
          style={{ width: '100%', height: 560, border: '1px solid var(--line)' }}
        />
      );
    }

    if (document.type === 'Protocol') {
      return (
        <>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: 17, fontWeight: 600, marginBottom: 8 }}>1. Study Title</h2>
          <p style={{ marginBottom: 14 }}>{study?.protocolTitle || 'Bioequivalence study protocol'}</p>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: 17, fontWeight: 600, marginBottom: 8 }}>2. Background</h2>
          <p style={{ marginBottom: 14 }}>{study?.molecule || 'The test product'} is being developed as a generic version of the reference product. This protocol describes the design and conduct of a bioequivalence study in healthy adult volunteers under fasted conditions.</p>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: 17, fontWeight: 600, marginBottom: 8 }}>3. Objectives</h2>
          <p style={{ marginBottom: 14 }}>Primary: To compare the rate and extent of absorption of the test and reference products under fasted conditions, based on Cmax and AUC.</p>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: 17, fontWeight: 600, marginBottom: 8 }}>4. Study Design</h2>
          <p>{study?.design || 'Open-label, randomized, balanced, two-treatment, two-period, two-sequence, single-dose, crossover, oral bioequivalence study.'}</p>
        </>
      );
    }

    if (document.type === 'Method Validation') {
      return (
        <>
          <h2 style={{ fontFamily: 'var(--display)', fontSize: 17, fontWeight: 600, marginBottom: 8 }}>Bioanalytical Method Validation Report</h2>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginBottom: 14 }}>
            <tbody>
              <tr><td style={{ padding: 6, color: 'var(--ink-3)', width: '35%' }}>Analyte</td><td style={{ padding: 6 }}>{study?.molecule || '—'}</td></tr>
              <tr><td style={{ padding: 6, color: 'var(--ink-3)' }}>Matrix</td><td style={{ padding: 6 }}>Human K2EDTA plasma</td></tr>
              <tr><td style={{ padding: 6, color: 'var(--ink-3)' }}>Technique</td><td style={{ padding: 6 }}>LC-MS/MS</td></tr>
              <tr><td style={{ padding: 6, color: 'var(--ink-3)' }}>LLOQ</td><td style={{ padding: 6 }}>0.5 ng/mL</td></tr>
              <tr><td style={{ padding: 6, color: 'var(--ink-3)' }}>Calibration range</td><td style={{ padding: 6 }}>0.5 – 500 ng/mL</td></tr>
            </tbody>
          </table>
          <h3 style={{ fontFamily: 'var(--sans)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6, color: 'var(--ink-2)' }}>Validation parameters</h3>
          <p>Selectivity, accuracy (within and between run), precision, recovery, matrix effect, stability (bench-top, freeze-thaw, long-term) all within acceptance criteria per CDSCO bioanalytical method validation guidance.</p>
        </>
      );
    }

    // Default preview
    return (
      <>
        <h2 style={{ fontFamily: 'var(--display)', fontSize: 17, fontWeight: 600, marginBottom: 8 }}>{document.name}</h2>
        <p style={{ marginBottom: 14, color: 'var(--ink-3)', fontSize: 12 }}>Type: {document.type} · Version: v{document.version}</p>
        <p>This is a {document.type.toLowerCase()} document for study {study?.code}. In a production deployment this preview would render the actual file content (PDF.js for PDFs, Office Online or Mammoth.js for Word documents, SheetJS for spreadsheets).</p>
        {document.tags && (Array.isArray(document.tags) ? document.tags.length > 0 : document.tags) && (
          <p style={{ marginTop: 14 }}>
            <strong>Tags:</strong> {Array.isArray(document.tags) ? document.tags.join(', ') : document.tags}
          </p>
        )}
      </>
    );
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal modal-wide"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 880, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-head">
          <div className="flex gap-3 center">
            <i className={`ti ${iconClass}`} style={{ fontSize: 24, color: iconColor }} />
            <div>
              <div className="modal-title" style={{ fontSize: 17 }}>{document.name}</div>
              <div className="xs muted">
                {document.type} · v{document.version} · {document.size} ·{' '}
                {document.status === 'approved' ? (
                  <span style={{ color: 'var(--ok)' }}>Approved</span>
                ) : document.status === 'review' ? (
                  <span style={{ color: 'var(--warn)' }}>Under review</span>
                ) : document.status}
              </div>
            </div>
          </div>
          <div className="flex gap-2 center">
            {document.fileUrl && (
              <a className="btn btn-sm" href={document.fileUrl} target="_blank" rel="noreferrer">
                <i className="ti ti-external-link" /> Open file
              </a>
            )}
            <button className="icon-btn" onClick={onClose}><i className="ti ti-x" /></button>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', background: 'var(--bg-deep)', padding: '24px' }}>
          <div style={{
            background: 'white',
            border: '1px solid var(--line)',
            padding: '48px 56px',
            fontFamily: 'var(--display)',
            fontSize: 13,
            lineHeight: 1.65,
            color: 'var(--ink)',
            minHeight: 420,
            boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
            maxWidth: 720,
            margin: '0 auto'
          }}>
            <div style={{
              borderBottom: '2px solid var(--accent)',
              paddingBottom: 10,
              marginBottom: 18,
              display: 'flex',
              justifyContent: 'space-between'
            }}>
              <div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.14em', marginBottom: 4 }}>
                  Arcolab Confidential · {study?.code || ''}
                </div>
                <div style={{ fontFamily: 'var(--display)', fontSize: 20, fontWeight: 600, color: 'var(--accent)', letterSpacing: '-0.01em' }}>
                  {document.type}
                </div>
              </div>
              <div style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.14em' }}>
                v{document.version}<br />{fmtDate(document.updatedAt)}
              </div>
            </div>

            {previewBody()}

            <div style={{ borderTop: '1px solid var(--line)', marginTop: 28, paddingTop: 12, fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'flex', justifyContent: 'space-between' }}>
              <span>Owner: {owner?.name || '—'}</span>
              <span>Page 1 of {document.pages || Math.floor(Math.random() * 30 + 8)}</span>
            </div>
          </div>
          <div className="xs muted mt-3" style={{ textAlign: 'center' }}>
            <i className="ti ti-info-circle" /> For non-PDF formats, summary preview is shown from metadata. Use Open file for original content.
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentPreviewModal;